import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ɵConsole,
} from '@angular/core';
import {
  Validators,
  FormControl,
  FormGroup,
  FormBuilder,
  AbstractControl,
  ValidatorFn,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AppConfig } from '../../../../app.config';
import { MessageService } from 'primeng/api';
import { RegistrationService } from '../../../../services/registration.service';
import { AuthenticationService } from '../../../../services/authentication.service';
import { HelpersService } from '../../../../services/helpers.service';
import { UserService } from '../../../../services/user.service';
import { Router, ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-wizard-account-verification',
  templateUrl: './wizard-account-verification.component.html',
  styleUrls: ['./wizard-account-verification.component.scss'],
})
export class WizardAccountVerificationComponent implements OnInit {
  @Input() messageService: MessageService;
  @Input() verificationCode;
  @Output() next = new EventEmitter();
  public VerificationForm: FormGroup;
  public loading = true;
  public saving = false;
  public passwordSet: boolean = false;
  public verified = false;
  public message = null;
  public passwordMessage = null;
  public partner: any = {};
  public showPasswordStrength: boolean = false;
  public returnURL: string = null;

  constructor(
    private appConfig: AppConfig,
    private fb: FormBuilder,
    private translate: TranslateService,
    private registrationService: RegistrationService,
    private authenticationService: AuthenticationService,
    private helpersService: HelpersService,
    private userService: UserService,
    public router: Router,
    public route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.verifyAccount();
  }

  verifyAccount() {
    this.registrationService.verifyAccount(this.verificationCode).subscribe(
      (partner) => {
        this.partner = partner;
        this.loading = false;
        this.verified = true;
        this.message = this.translate.instant('onboarding.messages.verified', {
          name: this.partner.UserName,
        });
        if (this.partner && this.partner.RequiresPasswordReset) {
          this.setupPasswordValidation();
        } else {
          this.setupLoginValidation();
        }
      },
      (err) => {
        this.loading = false;
        this.verified = false;
        this.message = this.translate.instant(
          'onboarding.messages.verification_failed',
        );
        this.setupPasswordValidation();
        this.VerificationForm.controls['Password'].disable();
        this.VerificationForm.controls['ConfirmPassword'].disable();
      },
    );
  }

  setupPasswordValidation() {
    this.VerificationForm = new FormGroup(
      {
        Password: new FormControl(
          '',
          Validators.compose([Validators.required, this.passwordCheck()]),
        ),
        ConfirmPassword: new FormControl(
          '',
          Validators.compose([Validators.required, this.checkEqual()]),
        ),
      },
      (formGroup: any) => {
        return this.areEqual(formGroup);
      },
    );
  }

  setupLoginValidation() {
    this.VerificationForm = new FormGroup({
      Password: new FormControl('', Validators.compose([Validators.required])),
      Email: new FormControl(
        this.partner.UserName,
        Validators.compose([Validators.required]),
      ),
    });
  }

  setPassword() {
    this.saving = true;
    this.passwordMessage = null;
    var data = {
      NewPassword: this.VerificationForm.value.Password,
      ConfirmPassword: this.VerificationForm.value.ConfirmPassword,
    };

    this.registrationService.setPassword(this.verificationCode, data).subscribe(
      (result) => {
        this.Authenticate();
      },
      (err) => {
        this.passwordSet = false;
        this.saving = false;
        this.passwordMessage = err.Message;
      },
    );
  }

  Authenticate() {
    this.saving = true;
    this.authenticationService
      .login(this.partner.Email, this.VerificationForm.value.Password)
      .subscribe(
        (auth) => {

          if (auth.shop2FAEnbaled || auth.user2FAEnabled == true) {
            this.authenticationService.username = this.partner.Email;
            this.authenticationService.password = this.VerificationForm.value.Password;
            this.authenticationService.returnUrl = this.returnURL;

            this.returnURL = encodeURIComponent(`/bikeshop-wizard?step=3&verificationCode=${this.verificationCode}`)

            //2FA should be used
            if (
              auth.shop2FAEnbaled == false &&
              auth.user2FAEnabled == true
            ) {
              //Send to 2FA code screen - User enabled 2FA and no shop has it on
              this.router.navigateByUrl('/2facode' + '?returnUrl='+this.returnURL);
              return;
            } else if (
              auth.shop2FAEnbaled == true &&
              auth.user2FAEnabled == true
            ) {
              //Send to 2FA code screen - Shop has 2FA on and user has 2FA registered
              this.router.navigateByUrl('/2facode' + '?returnUrl='+this.returnURL);
              return;
            } else {
              //User has not enabled 2FA, send to 2FA register screen
              this.router.navigateByUrl('/2fasetup'+'?returnUrl='+this.returnURL);
              return;
            }
          }

          this.helpersService.saveAuthData(auth);
          this.helpersService.saveAuthDataNew(auth);

          this.userService
            .getUserDetail(this.partner.Email)
            .subscribe((user) => {
              const profile = user;
              const roles = profile.UserRoles.filter((x) => {
                return x.PartnerID > 0;
              });
              var shops = roles
                .map((x) => {
                  return x.PartnerID;
                })
                .filter((value, index, self) => {
                  return self.indexOf(value) === index;
                });

              profile.PartnerID = this.partner.PartnerID;
              var shopRoles = roles
                .filter((x) => {
                  return x.PartnerID === profile.PartnerID;
                })
                .map((x) => {
                  return x.RoleID;
                });

              profile.isAdmin = true; //Default them to a manager role sice that would be there role anyway
              profile.isManager = true;
              profile.isTechnician = false;

              this.helpersService.saveProfileData(profile);

              this.userService.getUserRolesInsurance(profile.ID).subscribe(
                (permissions) => {
                  this.helpersService.saveProfileInsuranceRoles(permissions);
                },
                (error) => {},
              );
            });

          this.saving = false;
          this.passwordSet = true;
          this.next.emit(this.partner);
        },
        (err) => {
          this.saving = false;
          this.passwordSet = false;
          this.passwordMessage = this.translate.instant(
            'onboarding.messages.auth_failed',
          );
        },
      );
  }

  checkEqual(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (this.VerificationForm) {
        return control.value ===
          this.VerificationForm.controls['Password'].value
          ? null
          : { invalidPassword: true };
      }
    };
  }

  areEqual(formGroup: FormGroup) {
    let value;
    let valid = true;
    for (let key in formGroup.controls) {
      if (formGroup.controls.hasOwnProperty(key)) {
        let control: FormControl = <FormControl>formGroup.controls[key];

        if (value === undefined) {
          value = control.value;
        } else {
          if (value !== control.value) {
            valid = false;
            break;
          }
        }
      }
    }

    if (valid) {
      return null;
    }

    return {
      areEqual: true,
    };
  }

  passwordCheck(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      return this.measureStrength(control.value) >= 70
        ? null
        : { invalidPassword: true };
    };
  }

  checkStrength(control) {
    console.log(control);
    return this.measureStrength(control.controls.Password.value);
  }

  measureStrength(pass: string) {
    let score = 0;
    let letters = {};
    for (let i = 0; i < pass.length; i++) {
      letters[pass[i]] = (letters[pass[i]] || 0) + 1;
      score += 5.0 / letters[pass[i]];
    }

    let variations = {
      digits: /\d/.test(pass),
      lower: /[a-z]/.test(pass),
      upper: /[A-Z]/.test(pass),
      nonWords: /\W/.test(pass),
    };
    let variationCount = 0;
    for (let check in variations) {
      variationCount += variations[check] ? 1 : 0;
    }
    score += (variationCount - 1) * 10;
    return Math.trunc(score);
  }

  changeToResetPassword() {
    this.setupPasswordValidation();
    this.partner.RequiresPasswordReset = true;
  }
}
